// Passengers (the keke's day job): people wait at the stops, a keke pulls up and stops to load them,
// carries them across the route and drops them where they are going, and each one pays a few coins
// at the drop. Everyone on the road carries, agberos too, so carrying never tells the sides apart;
// it never moves the route bar or decides a win. Riders spawn from their own seeded generator, so
// turning passengers on changes no other draw in the match. Pure and deterministic like the rest.
import { getMap, TILE } from '@wahala/maps';
import { dist2 } from './geometry.js';
import { nextInt, nextU32, seedRng } from './rng.js';
/** A keke carries three passengers. */
export const SEATS = 3;
/** At most this many wait at one stop. */
export const WAITING_PER_STOP = 2;
/** A new passenger turns up this often (while the road has room for one). */
export const RIDE_SPAWN_MS = 4_000;
/** Close enough to a stop to load or drop (the same reach as a stop's job). */
export const RIDE_RADIUS = 1.5 * TILE;
/** Slow enough to count as stopped (world units per tick; full speed is 22). */
export const STOPPED_SPEED = 4;
/** Fares, in coins: the short hop and the long haul. */
export const FARE_MIN = 2;
export const FARE_MAX = 6;
/** One more coin of fare per this much straight-line distance. */
const FARE_STEP = 8 * TILE;
/** What one round of passengers can pay one player, at most. */
export const FARES_PER_ROUND_MAX = 120;
/** The stops passengers use: every station that is a stop, in map order. */
export const rideStops = (map) => map.stations.filter((st) => st.kind === 'stop');
/** How many may wait on the whole road at once: enough that a keke never drives far for nobody. */
const roadCap = (s, stops) => Math.min(stops * WAITING_PER_STOP, Math.max(4, s.order.length + 2));
export function fareFor(from, to) {
    const d = Math.sqrt(dist2(from, to));
    return Math.max(FARE_MIN, Math.min(FARE_MAX, FARE_MIN + Math.floor(d / FARE_STEP)));
}
/** Rides for a match whose mode carries passengers (the rule set says), seeded apart from the match. */
export function createRides(s) {
    const r = {
        rng: seedRng((s.seed ^ 0x52494445) >>> 0),
        nextId: 1,
        nextAt: s.now + RIDE_SPAWN_MS,
        waiting: {},
        aboard: {},
        earned: {},
        trips: {},
    };
    for (const id of s.order) {
        r.aboard[id] = [];
        r.earned[id] = 0;
        r.trips[id] = 0;
    }
    const map = getMap(s.mapId);
    for (const st of rideStops(map))
        r.waiting[st.id] = [];
    // the road starts with people already waiting
    const start = Math.ceil(roadCap(s, rideStops(map).length) / 2);
    for (let i = 0; i < start; i++)
        spawnRider(r, map);
    return r;
}
const waitingCount = (r) => Object.values(r.waiting).reduce((n, list) => n + list.length, 0);
/** One passenger at a stop with room, going somewhere else; both picked with the rides' generator. */
function spawnRider(r, map) {
    const stops = rideStops(map);
    if (stops.length < 2)
        return;
    const open = stops.filter((st) => (r.waiting[st.id]?.length ?? 0) < WAITING_PER_STOP);
    if (open.length === 0)
        return;
    const from = open[nextInt(r.rng, open.length)];
    const others = stops.filter((st) => st.id !== from.id);
    const to = others[nextInt(r.rng, others.length)];
    r.waiting[from.id] = [
        ...(r.waiting[from.id] ?? []),
        { id: `r${r.nextId++}`, to: to.id, fare: fareFor(from.pos, to.pos) },
    ];
}
/** Each tick on the road: a passenger turns up now and then. */
export function tickRides(s) {
    const r = s.rides;
    if (!r || s.now < r.nextAt)
        return;
    r.nextAt = s.now + RIDE_SPAWN_MS;
    const map = getMap(s.mapId);
    if (waitingCount(r) < roadCap(s, rideStops(map).length))
        spawnRider(r, map);
    // keep the generator moving every spawn tick, full road or not, so later draws never depend on it
    nextU32(r.rng);
}
/** The stop this keke is pulled up at (the nearest in reach), or null. */
export function stopAt(map, pos) {
    let best = null;
    let bestD = RIDE_RADIUS * RIDE_RADIUS;
    for (const st of rideStops(map)) {
        const d = dist2(pos, st.pos);
        if (d <= bestD) {
            best = st;
            bestD = d;
        }
    }
    return best;
}
export const stopped = (p) => p.vel.x * p.vel.x + p.vel.y * p.vel.y <= STOPPED_SPEED * STOPPED_SPEED;
/**
 * Why this keke may not load or drop here now, or null. Range and speed come before "nothing to
 * do", so the button stays on the road while the keke is between stops.
 */
export function checkRide(s, p) {
    const r = s.rides;
    if (!r)
        return 'NOT_IN_MODE';
    if (s.phase !== 'PLAYING')
        return 'WRONG_PHASE';
    if (!p.alive)
        return 'NOT_ALIVE';
    if (p.vent !== null)
        return 'IN_VENT';
    const st = stopAt(getMap(s.mapId), p.pos);
    if (!st)
        return 'OUT_OF_RANGE';
    if (!stopped(p))
        return 'MOVING';
    const aboard = r.aboard[p.id] ?? [];
    const dropping = aboard.some((x) => x.to === st.id);
    const loading = aboard.length < SEATS && (r.waiting[st.id]?.length ?? 0) > 0;
    return dropping || loading ? null : 'NOTHING_HERE';
}
/** At a stop, stopped: drop everyone going here (they pay), then load whoever fits. */
export function rideStop(s, p, events) {
    const r = s.rides;
    const st = stopAt(getMap(s.mapId), p.pos);
    const aboard = r.aboard[p.id] ?? [];
    const off = aboard.filter((x) => x.to === st.id);
    if (off.length) {
        const room = FARES_PER_ROUND_MAX - (r.earned[p.id] ?? 0);
        const coins = Math.max(0, Math.min(room, off.reduce((n, x) => n + x.fare, 0)));
        r.aboard[p.id] = aboard.filter((x) => x.to !== st.id);
        r.earned[p.id] = (r.earned[p.id] ?? 0) + coins;
        r.trips[p.id] = (r.trips[p.id] ?? 0) + off.length;
        events.push({ t: 'ride.paid', playerId: p.id, stationId: st.id, riders: off.length, coins });
    }
    const seats = SEATS - (r.aboard[p.id] ?? []).length;
    const queue = r.waiting[st.id] ?? [];
    const on = queue.splice(0, Math.max(0, seats));
    if (on.length) {
        r.aboard[p.id] = [...(r.aboard[p.id] ?? []), ...on];
        events.push({ t: 'ride.boarded', playerId: p.id, stationId: st.id, riders: on.length });
    }
}
/** A knocked or dropped keke's passengers run off: nobody pays for that trip. */
export function emptyKeke(s, id) {
    if (s.rides)
        s.rides.aboard[id] = [];
}
/** A hard crash shakes the last passenger out (they walk off without paying). */
export function shakeOut(s, id, events) {
    const list = s.rides?.aboard[id];
    if (!list?.length)
        return;
    list.pop();
    events.push({ t: 'ride.lost', playerId: id, riders: 1 });
}
/** Your own riders, for your view: where each is going and what they pay. */
export const ridersOf = (s, id) => s.rides?.aboard[id] ?? [];
//# sourceMappingURL=rides.js.map